import { useState } from 'react';
import { Archive, ChevronDown, Pencil, Plus, Trash2, Users } from 'lucide-react';
import type { Team, User } from '@foodstyles/shared';
import { useAuth } from '../../context/AuthContext';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useToast } from '../../context/ToastContext';
import { ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import {
  Avatar,
  Btn,
  Card,
  EmptyState,
  Field,
  Modal,
  RoleBadge,
  SectionTitle,
  Spinner,
  TextInput,
} from '../../components/primitives';
import {
  useInvites,
  useManageMutations,
  useProjectTasks,
  useProjects,
  useTeams,
  useUsers,
} from './useManage';
import type { ManageProject, ManageTeam } from '../../types/api';

type Tab = 'teams' | 'projects' | 'members';

export function ManageScreen() {
  const { permissions } = useAuth();
  const [tab, setTab] = useState<Tab>('teams');
  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'teams', label: 'Teams', show: true },
    { id: 'projects', label: 'Projects & Tasks', show: true },
    { id: 'members', label: 'Members', show: !!permissions?.manageUsers },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex items-center gap-2">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-semibold',
                tab === t.id ? 'bg-ink text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700',
              )}
            >
              {t.label}
            </button>
          ))}
      </div>
      {tab === 'teams' && <TeamsPanel />}
      {tab === 'projects' && <ProjectsPanel />}
      {tab === 'members' && permissions?.manageUsers && <MembersPanel />}
    </div>
  );
}

// ── Team multi-select chips ─────────────────────────────────────────────────
function TeamMultiSelect({ teams, value, onChange }: { teams: Team[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {teams.map((t) => {
        const on = value.includes(t.id);
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(on ? value.filter((x) => x !== t.id) : [...value, t.id])}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors',
              on ? 'bg-[#FF5A3C]/12 text-[#D8341A] dark:text-[#FF8A6B] ring-[#FF5A3C]/30' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 ring-slate-200 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700',
            )}
          >
            {t.name}
          </button>
        );
      })}
    </div>
  );
}

function teamNames(teams: Team[], ids: string[]): string {
  return ids.map((id) => teams.find((t) => t.id === id)?.name).filter(Boolean).join(', ') || '—';
}

// ── Teams ────────────────────────────────────────────────────────────────────
function TeamsPanel() {
  const teamsQuery = useTeams();
  const boot = useBootstrap();
  const m = useManageMutations();
  const toast = useToast();
  const [name, setName] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [membersOf, setMembersOf] = useState<ManageTeam | null>(null);

  const onError = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Action failed.');
  const addTeam = () => {
    if (!name.trim()) {
      setInvalid(true); // flag the empty field so it's clear why nothing happened
      return;
    }
    m.createTeam.mutate(name.trim(), { onSuccess: () => setName(''), onError });
  };

  return (
    <Card pad={false}>
      <div className="flex items-center gap-2 px-5 py-4">
        <SectionTitle sub='"Everyone" is built-in and contains all users.'>Teams</SectionTitle>
        <div className="ml-auto flex items-center gap-2">
          <TextInput
            className="w-48"
            placeholder="New team name"
            value={name}
            invalid={invalid}
            onChange={(e) => {
              setName(e.target.value);
              if (invalid) setInvalid(false);
            }}
            onKeyDown={(e) => e.key === 'Enter' && addTeam()}
          />
          <Btn variant="primary" onClick={addTeam}>
            <Plus size={15} /> Add
          </Btn>
        </div>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-700">
        {teamsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (
          teamsQuery.data!.teams.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-5 py-3">
              {t.system ? (
                <span className="flex-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{t.name}</span>
              ) : (
                <input
                  defaultValue={t.name}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== t.name) m.renameTeam.mutate({ id: t.id, name: v }, { onError });
                  }}
                  className="flex-1 rounded-md px-2 py-1 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-[#FF5A3C]/20"
                />
              )}
              <span className="text-xs text-slate-400">{t.userCount} members</span>
              <Btn variant="ghost" size="sm" onClick={() => setMembersOf(t)} disabled={t.system}>
                <Users size={14} /> Members
              </Btn>
              <button
                disabled={t.system}
                onClick={() => confirm(`Delete team "${t.name}"?`) && m.deleteTeam.mutate(t.id, { onError })}
                className="rounded p-1.5 text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-30"
                aria-label="Delete team"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>

      <Modal open={!!membersOf} onClose={() => setMembersOf(null)} title={`Members — ${membersOf?.name ?? ''}`}>
        <div className="max-h-[55vh] space-y-1 overflow-y-auto">
          {(boot.data?.users ?? []).map((u) => {
            const on = u.teamIds.includes(membersOf?.id ?? '');
            return (
              <label key={u.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50 dark:hover:bg-slate-700">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => membersOf && m.toggleMember.mutate({ teamId: membersOf.id, userId: u.id }, { onError })}
                  className="h-4 w-4 accent-[#FF5A3C]"
                />
                <Avatar name={u.name} size={30} />
                <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-100">{u.name}</span>
                <RoleBadge role={u.role} sm />
              </label>
            );
          })}
        </div>
      </Modal>
    </Card>
  );
}

// ── Projects & Tasks ───────────────────────────────────────────────────────────
function ProjectsPanel() {
  const projectsQuery = useProjects();
  const boot = useBootstrap();
  const m = useManageMutations();
  const toast = useToast();
  const teams = boot.data?.teams ?? [];
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [projModal, setProjModal] = useState<{ project?: ManageProject } | null>(null);
  const [taskModal, setTaskModal] = useState<{ projectId: string; defaultTeams: string[]; task?: { id: string; name: string; teamIds: string[] } } | null>(null);

  const onError = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Action failed.');
  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Btn variant="primary" onClick={() => setProjModal({})}>
          <Plus size={15} /> New project
        </Btn>
      </div>
      {projectsQuery.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        projectsQuery.data!.projects.map((p) => (
          <ProjectCard
            key={p.id}
            project={p}
            teams={teams}
            isOpen={open.has(p.id)}
            onToggle={() => toggle(p.id)}
            onEdit={() => setProjModal({ project: p })}
            onArchive={() => m.archiveProject.mutate(p.id, { onError })}
            onAddTask={() => setTaskModal({ projectId: p.id, defaultTeams: p.teamIds })}
            onEditTask={(task) => setTaskModal({ projectId: p.id, defaultTeams: p.teamIds, task })}
            onArchiveTask={(id) => m.archiveTask.mutate({ id, projectId: p.id }, { onError })}
          />
        ))
      )}

      {projModal && (
        <ProjectModal
          teams={teams}
          project={projModal.project}
          onClose={() => setProjModal(null)}
          onSave={(name, teamIds) => {
            const opts = { onSuccess: () => setProjModal(null), onError };
            if (projModal.project) m.updateProject.mutate({ id: projModal.project.id, name, teamIds }, opts);
            else m.createProject.mutate({ name, teamIds }, opts);
          }}
        />
      )}
      {taskModal && (
        <TaskModal
          teams={teams}
          defaultTeams={taskModal.defaultTeams}
          task={taskModal.task}
          onClose={() => setTaskModal(null)}
          onSave={(name, teamIds) => {
            const opts = { onSuccess: () => setTaskModal(null), onError };
            if (taskModal.task) m.updateTask.mutate({ id: taskModal.task.id, projectId: taskModal.projectId, name, teamIds }, opts);
            else m.createTask.mutate({ projectId: taskModal.projectId, name, teamIds }, opts);
          }}
        />
      )}
    </div>
  );
}

function ProjectCard({
  project,
  teams,
  isOpen,
  onToggle,
  onEdit,
  onArchive,
  onAddTask,
  onEditTask,
  onArchiveTask,
}: {
  project: ManageProject;
  teams: Team[];
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onAddTask: () => void;
  onEditTask: (task: { id: string; name: string; teamIds: string[] }) => void;
  onArchiveTask: (id: string) => void;
}) {
  const tasksQuery = useProjectTasks(project.id, isOpen);
  return (
    <Card pad={false} className={cn('overflow-hidden', project.archived && 'opacity-70')}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={onToggle} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Toggle tasks">
          <ChevronDown size={18} className={cn('transition-transform', !isOpen && '-rotate-90')} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{project.name}</span>
            {project.archived && (
              <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400">Archived</span>
            )}
          </div>
          <span className="text-xs text-slate-400">{teamNames(teams, project.teamIds)}</span>
        </div>
        <span className="text-xs text-slate-400">{project.activeTaskCount} tasks</span>
        <Btn variant="ghost" size="sm" onClick={onEdit}>
          <Pencil size={14} />
        </Btn>
        <Btn variant="ghost" size="sm" onClick={onArchive}>
          <Archive size={14} /> {project.archived ? 'Unarchive' : 'Archive'}
        </Btn>
      </div>
      {isOpen && (
        <div className="border-t border-slate-100 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/40 px-4 py-3">
          {tasksQuery.isLoading ? (
            <Spinner />
          ) : (
            <div className="space-y-1">
              {tasksQuery.data!.tasks.map((t) => (
                <div key={t.id} className={cn('flex items-center gap-2 rounded-lg px-2 py-1.5', t.archived && 'opacity-60')}>
                  <span className="text-sm text-slate-700 dark:text-slate-200">{t.name}</span>
                  {t.archived && <span className="rounded bg-slate-200 dark:bg-slate-700 px-1.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400">archived</span>}
                  <span className="text-xs text-slate-400">{teamNames(teams, t.teamIds)}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => onEditTask({ id: t.id, name: t.name, teamIds: t.teamIds })} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Edit task">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => onArchiveTask(t.id)} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Archive task">
                      <Archive size={13} />
                    </button>
                  </div>
                </div>
              ))}
              <Btn variant="subtle" size="sm" className="mt-1" onClick={onAddTask}>
                <Plus size={13} /> Add task
              </Btn>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ProjectModal({
  teams,
  project,
  onClose,
  onSave,
}: {
  teams: Team[];
  project?: ManageProject;
  onClose: () => void;
  onSave: (name: string, teamIds: string[]) => void;
}) {
  const [name, setName] = useState(project?.name ?? '');
  const [teamIds, setTeamIds] = useState<string[]>(project?.teamIds ?? []);
  return (
    <Modal open onClose={onClose} title={project ? 'Edit project' : 'New project'}>
      <div className="space-y-4">
        <Field label="Name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Teams">
          <TeamMultiSelect teams={teams} value={teamIds} onChange={setTeamIds} />
        </Field>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={!name.trim()} onClick={() => onSave(name.trim(), teamIds)}>
            Save
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function TaskModal({
  teams,
  defaultTeams,
  task,
  onClose,
  onSave,
}: {
  teams: Team[];
  defaultTeams: string[];
  task?: { id: string; name: string; teamIds: string[] };
  onClose: () => void;
  onSave: (name: string, teamIds: string[]) => void;
}) {
  const [name, setName] = useState(task?.name ?? '');
  const [teamIds, setTeamIds] = useState<string[]>(task?.teamIds ?? defaultTeams);
  return (
    <Modal open onClose={onClose} title={task ? 'Edit task' : 'New task'}>
      <div className="space-y-4">
        <Field label="Name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Teams" hint="Defaults to the project's teams, then independent.">
          <TeamMultiSelect teams={teams} value={teamIds} onChange={setTeamIds} />
        </Field>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={!name.trim()} onClick={() => onSave(name.trim(), teamIds)}>
            Save
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Members (Admin) ────────────────────────────────────────────────────────────
function MembersPanel() {
  const usersQuery = useUsers();
  const invitesQuery = useInvites();
  const boot = useBootstrap();
  const { user: me } = useAuth();
  const m = useManageMutations();
  const toast = useToast();
  const teams = boot.data?.teams ?? [];
  const onError = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Action failed.');
  const [invEmail, setInvEmail] = useState('');
  const [invRole, setInvRole] = useState('Member');
  const [editingUser, setEditingUser] = useState<User | null>(null);

  if (usersQuery.isLoading)
    return (
      <Card>
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </Card>
    );

  const { users, activeAdmins } = usersQuery.data!;
  const invites = invitesQuery.data?.invites ?? [];
  const sendInvite = () => {
    const email = invEmail.trim().toLowerCase();
    if (!email.includes('@')) return;
    m.invite.mutate(
      { email, role: invRole },
      { onSuccess: () => setInvEmail(''), onError },
    );
  };

  return (
    <Card pad={false}>
      <div className="px-5 py-4">
        <SectionTitle sub="Invite-only: people can sign in with Google only after you invite their email here.">
          Members
        </SectionTitle>
      </div>

      {/* Invite form */}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 dark:border-slate-700 px-5 py-3">
        <TextInput
          type="email"
          placeholder="teammate@example.com"
          value={invEmail}
          onChange={(e) => setInvEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendInvite()}
          className="w-64"
        />
        <select
          value={invRole}
          onChange={(e) => setInvRole(e.target.value)}
          className="rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-2 text-sm"
        >
          {['Member', 'Manager', 'Admin'].map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
        <Btn variant="primary" onClick={sendInvite} disabled={!invEmail.includes('@') || m.invite.isPending}>
          <Plus size={15} /> Invite
        </Btn>
      </div>

      {/* Outstanding invites */}
      {invites.length > 0 && (
        <div className="border-t border-slate-100 dark:border-slate-700 bg-amber-50/40 dark:bg-amber-500/10 px-5 py-2.5">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
            Invited — awaiting first sign-in
          </p>
          {invites.map((i) => (
            <div key={i.email} className="flex items-center gap-2 py-1">
              <span className="text-sm text-slate-700 dark:text-slate-200">{i.email}</span>
              <RoleBadge role={i.role} sm />
              <button
                onClick={() => m.removeInvite.mutate(i.email, { onError })}
                aria-label={`Remove invite for ${i.email}`}
                title="Remove invite"
                className="ml-auto rounded p-1 text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="divide-y divide-slate-100 dark:divide-slate-700">
        {users.map((u) => {
          const self = u.id === me?.id;
          const lastAdmin = u.role === 'Admin' && u.status === 'Active' && activeAdmins <= 1;
          const locked = self || lastAdmin;
          const reason = self ? "You can't change your own access" : lastAdmin ? 'At least one active Admin is required' : '';
          const disabled = u.status === 'Disabled';
          return (
            <div key={u.id} className={cn('flex items-center gap-3 px-5 py-3', disabled && 'bg-slate-50/70 dark:bg-slate-800/60')}>
              <Avatar name={u.name} size={36} grayscale={disabled} />
              <div className="min-w-0 flex-1">
                <div className={cn('flex items-center gap-2 text-sm font-semibold', disabled ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-100')}>
                  {u.name}
                  {self && <span className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400">You</span>}
                  {disabled && <span className="rounded bg-slate-200 dark:bg-slate-700 px-1.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400">Disabled</span>}
                </div>
                <span className="text-xs text-slate-400">{teamNames(teams, u.teamIds)}</span>
              </div>
              <button
                onClick={() => setEditingUser(u)}
                aria-label={`Edit ${u.name}`}
                title="Edit name / email"
                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <Pencil size={15} />
              </button>
              <select
                value={u.role}
                disabled={locked}
                title={reason}
                onChange={(e) => m.setRole.mutate({ id: u.id, role: e.target.value }, { onError })}
                className="rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-sm disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:text-slate-400"
              >
                {['Admin', 'Manager', 'Member'].map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
              <Btn
                variant={disabled ? 'subtle' : 'secondary'}
                size="sm"
                disabled={locked}
                title={reason}
                className="w-20"
                onClick={() => m.toggleDisabled.mutate(u.id, { onError })}
              >
                {disabled ? 'Enable' : 'Disable'}
              </Btn>
              <button
                disabled={locked}
                title={reason || 'Remove access'}
                onClick={() => confirm(`Remove ${u.name}? Their history is kept but anonymized.`) && m.deleteUser.mutate(u.id, { onError })}
                className="rounded p-1.5 text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-30"
                aria-label="Remove member"
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>

      {editingUser && (
        <MemberEditModal
          member={editingUser}
          self={editingUser.id === me?.id}
          saving={m.updateUser.isPending}
          onClose={() => setEditingUser(null)}
          onSave={(name, email) =>
            m.updateUser.mutate(
              { id: editingUser.id, name, email },
              { onSuccess: () => setEditingUser(null), onError },
            )
          }
        />
      )}
    </Card>
  );
}

function MemberEditModal({
  member,
  self,
  saving,
  onClose,
  onSave,
}: {
  member: User;
  self: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (name: string, email: string) => void;
}) {
  const [name, setName] = useState(member.name);
  const [email, setEmail] = useState(member.email);
  const valid = name.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <Modal open onClose={onClose} title={`Edit member — ${member.name}`}>
      <div className="space-y-4">
        <Field label="Name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field
          label="Email"
          hint={
            self
              ? "You can't change your own email."
              : 'The email is their Google sign-in. Changing it re-links this member to the new address and signs them out.'
          }
        >
          <TextInput type="email" value={email} disabled={self} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            disabled={!valid || saving}
            onClick={() => onSave(name.trim(), email.trim().toLowerCase())}
          >
            Save
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
